/* eslint-disable no-continue,unicorn/text-encoding-identifier-case */
// noinspection UnnecessaryContinueJS

import iconv from 'iconv-lite';

/**
 * EML format parser. EML is raw e-mail message header + body as returned by POP3 protocol.
 * RFC 822: http://www.ietf.org/rfc/rfc0822.txt
 * RFC 1521: https://www.ietf.org/rfc/rfc1521.txt
 *
 * @typedef {Object} EmlAttachment
 * @property {string} id
 * @property {string} contentType
 * @property {string} name
 * @property {boolean} inline
 * @property {string} data
 */

// Default character set
const defaultCharset = 'utf-8'; // to use if charset=... is missing

/**
 * Gets the character encoding name for iconv, e.g. 'iso-8859-2' -> 'iso88592'
 *
 * @param {string} charset
 * @returns {string} character encoding name
 */
const getCharsetName = charset => {
	const charsetName = charset.toLowerCase().replace(/[^\da-z]/g, '');
	return charsetName === 'none' ? 'utf8' : charsetName;
};

/**
 * Generates a random id
 *
 * @returns {string} random id
 */
const guid = () => 'xxxxxxxxxxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
	const r = Math.trunc(Math.random() * 16);
	const v = c === 'x' ? r : ((r & 0x3) | 0x8);
	return v.toString(16);
}).replace('-', '');

/**
 * @param {any[]} lines
 * @param {any} start
 * @param {any} parent
 * @param {any} options
 */
const parseRecursive = (lines, start, parent, options) => {
	/** @type {any} */
	let boundary = null;

	let lastHeaderName = '';
	let findBoundary = '';
	let insideBody = false;
	let insideBoundary = false;
	let isMultiHeader = false;
	let isMultipart = false;

	parent.headers = {};
	// parent.body = null;

	/**
	 * @param {any} cboundary
	 */
	const complete = cboundary => {
		// boundary.part = boundary.lines.join("\r\n");
		cboundary.part = {};
		parseRecursive(cboundary.lines, 0, cboundary.part, options);
		delete cboundary.lines;
	};

	// Read line by line
	for (let index = start; index < lines.length; index++) {
		/** @type {any} */
		const line = lines[index];

		// Header
		if (!insideBody) {
			// Search for empty line
			if (line === '') {
				insideBody = true;

				if (options && options.headersOnly) {
					break;
				}

				// Expected boundary
				const ct = parent.headers['Content-Type'];
				if (ct && /^multipart\//g.test(ct)) {
					// eslint-disable-next-line no-use-before-define
					const b = EmlParser.getBoundary(ct);
					if (b && b.length > 0) {
						findBoundary = b;
						isMultipart = true;
						parent.body = [];
					}
					// eslint-disable-next-line no-use-before-define
					else if (EmlParser.verbose) {
						console.warn(`Multipart without boundary! ${ct.replace(/\r?\n/g, ' ')}`);
					}
				}

				continue;
			}

			// Header value with new line
			let match = /^\s+([^\n\r]+)/g.exec(line);
			if (match) {
				if (isMultiHeader) {
					parent.headers[lastHeaderName][parent.headers[lastHeaderName].length - 1] += `\r\n${match[1]}`;
				}
				else {
					parent.headers[lastHeaderName] += `\r\n${match[1]}`;
				}
				continue;
			}

			// Header name and value
			match = /^([\w-]+):\s+([^\n\r]+)/gi.exec(line);
			if (match) {
				lastHeaderName = match[1];
				if (parent.headers[lastHeaderName]) {
					// Multiple headers with the same name
					isMultiHeader = true;
					if (typeof parent.headers[lastHeaderName] === 'string') {
						parent.headers[lastHeaderName] = [parent.headers[lastHeaderName]];
					}
					parent.headers[lastHeaderName].push(match[2]);
				}
				else {
					// Header first appeared here
					isMultiHeader = false;
					parent.headers[lastHeaderName] = match[2];
				}
				continue;
			}
		}
		// Body
		else if (isMultipart) {
			// Multipart body
			// Search for boundary start

			// Updated on 2019-10-12: A line before the boundary marker is not required to be an empty line
			// if (lines[i - 1] == "" && line.indexOf("--" + findBoundary) == 0 && !/\-\-(\r?\n)?$/g.test(line)) {
			if (line.indexOf(`--${findBoundary}`) === 0 && !/^--(\r?\n)?$/g.test(line)) {
				insideBoundary = true;

				// Complete the previous boundary
				if (boundary && boundary.lines) {
					complete(boundary);
				}

				// Start a new boundary
				/** @type {any} */
				const match = /^--([^\n\r]+)(\r?\n)?$/g.exec(line);
				boundary = { boundary: match[1], lines: [] };
				parent.body.push(boundary);

				// eslint-disable-next-line no-use-before-define
				if (EmlParser.verbose) {
					// eslint-disable-next-line no-console
					console.log(`Found boundary: ${boundary.boundary}`);
				}

				continue;
			}

			if (boundary && insideBoundary) {
				// Search for boundary end
				if (boundary.boundary && lines[index - 1] === '' && line.indexOf(`--${findBoundary}--`) === 0) {
					insideBoundary = false;
					complete(boundary);
					continue;
				}
				if (!boundary.lines) {
					boundary.lines = [];
				}
				boundary.lines.push(line);
			}
		}
		else {
			// Solid string body
			parent.body = lines.splice(index).join('\r\n');
			break;
		}
	}

	// Complete the last boundary
	if (parent.body && parent.body.length > 0 && parent.body[parent.body.length - 1].lines) {
		complete(parent.body[parent.body.length - 1]);
	}
};

/**
 * Word-wrap the string 's' to 'i' chars per row
 *
 * @param {string} s
 * @param {number} index
 * @returns {string} string wrapped
 */
const wrap = (s, index) => {
	const a = [];
	do {
		a.push(s.slice(0, Math.max(0, index)));
	}
	// eslint-disable-next-line no-cond-assign,no-param-reassign,unicorn/prefer-string-slice
	while ((s = s.substring(index, s.length)) !== '');
	return a.join('\r\n');
};

/**
 * Appends the boundary to the result
 *
 * @param {any} result
 * @param {any} headers
 * @param {string} content
 */
const appendsBoundary = (result, headers, content) => {
	const contentType = headers['Content-Type'];
	// eslint-disable-next-line no-use-before-define
	const charset = getCharsetName(EmlParser.getCharset(contentType) || defaultCharset);
	let encoding = headers['Content-Transfer-Encoding'];

	result.charset = charset;

	if (typeof encoding === 'string') {
		encoding = encoding.toLowerCase();
	}

	if (encoding === 'base64') {
		// @ts-ignore
		// eslint-disable-next-line no-param-reassign
		content = contentType.includes('gbk')
			? Buffer.from(iconv.decode(Buffer.from(content, 'base64'), 'gb2312'), 'utf8')
			: Buffer.from(content.replace(/\r?\n/g, ''), 'base64');
	}
	else if (encoding === 'quoted-printable') {
		// eslint-disable-next-line no-param-reassign,no-use-before-define
		content = EmlParser.unquotePrintable(content, charset);
	}
	else if (charset !== 'utf8' && encoding && (encoding.startsWith('binary') || encoding.startsWith('8bit'))) {
		// "8bit", "binary", "8bitmime", "binarymime"
		// eslint-disable-next-line no-param-reassign
		content = iconv.decode(Buffer.from(content, 'binary'), charset);
	}
	else if (!encoding && contentType?.includes('multipart/alternative') && Array.isArray(content)) {
		for (const boundaryData of content) {
			if (boundaryData?.part?.headers['Content-Type']) {
				appendsBoundary(result, boundaryData.part.headers, boundaryData.part.body);
			}
		}
	}

	if (!result.html && contentType && contentType.includes('text/html')) {
		if (typeof content !== 'string') {
			// content = content.toString("utf8");
			// eslint-disable-next-line no-param-reassign
			content = iconv.decode(Buffer.from(content), charset);
		}
		// Message in HTML format
		result.html = content;
	}
	else if (!result.text && contentType && contentType.includes('text/plain')) {
		if (typeof content !== 'string') {
			// content = content.toString("utf8");
			// eslint-disable-next-line no-param-reassign
			content = iconv.decode(Buffer.from(content), charset);
		}
		// Plain text message
		result.text = content;
	}
	else {
		// Get the attachment
		if (!result.attachments) {
			result.attachments = [];
		}

		/** @type {EmlAttachment} */
		const attachment = {};

		const id = headers['Content-ID'];
		if (id) {
			attachment.id = id;
		}

		let name = headers['Content-Disposition'] || headers['Content-Type'];

		const ct = headers['Content-Type'];
		if (ct) {
			const act = ct.split(/;\r?\n?\s*/g);
			attachment.contentType = act.shift();
			act.map((/** @type {string} */ a) => {
				const r = /^(([^=]+)=([^\n\r;]+));?\r?\n?/.exec(a);
				if (r) {
					if (r[2] === 'name') {
						name = r[1];
					}
					else {
						headers[r[2]] = r[3];
					}
				}
				return a;
			});
		}

		if (name) {
			const match = /name=\\?"?(.+?)\\?"?$/gi.exec(name);
			// eslint-disable-next-line no-use-before-define
			name = match ? EmlParser.unquoteString(match[1]) : null;
		}
		if (name) {
			attachment.name = name;
		}

		const cd = headers['Content-Disposition'];
		if (cd) {
			attachment.inline = /^\s*inline/g.test(cd);
		}

		attachment.data = content;
		result.attachments.push(attachment);
	}
};

// Overridable properties and functions
const EmlParser = {
	/**
	 * @param {any} data
	 * @param {any} options
	 * @param {Function} [callback]
	 */
	build: (data, options, callback) => {
		// Shift arguments
		if (typeof options === 'function' && callback === undefined) {
			// eslint-disable-next-line no-param-reassign
			callback = options;
			// eslint-disable-next-line no-param-reassign
			options = null;
		}

		if (typeof callback !== 'function') {
			// eslint-disable-next-line no-param-reassign
			callback = () => {};
		}

		let eml = '';
		const EOL = '\r\n'; // End-of-line

		try {
			if (!data || typeof data !== 'object') {
				throw new Error("Argument 'data' expected to be an object!");
			}

			if (!data.headers) {
				data.headers = {};
			}

			if (typeof data.subject === 'string') {
				data.headers.Subject = data.subject;
			}

			if (data.from !== undefined) {
				data.headers.From = (typeof data.from === 'string' ? data.from : EmlParser.toEmailAddress(data.from));
			}

			if (data.to !== undefined) {
				data.headers.To = (typeof data.to === 'string' ? data.to : EmlParser.toEmailAddress(data.to));
			}

			if (data.cc !== undefined) {
				data.headers.Cc = (typeof data.cc === 'string' ? data.cc : EmlParser.toEmailAddress(data.cc));
			}

			if (data.bcc !== undefined) {
				data.headers.BCc = (typeof data.bcc === 'string' ? data.bcc : EmlParser.toEmailAddress(data.bcc));
			}

			if (!data.headers.To) {
				throw new Error("Missing 'To' e-mail address!");
			}

			let boundary = `----=${guid()}`;
			if (data.headers['Content-Type'] === undefined) {
				data.headers['Content-Type'] = `multipart/mixed;${EOL}boundary="${boundary}"`;
			}
			else {
				const name = EmlParser.getBoundary(data.headers['Content-Type']);
				if (name) {
					boundary = name;
				}
			}

			// Build headers
			const keys = Object.keys(data.headers);
			for (const key of keys) {
				const value = data.headers[key];
				if (value === undefined) {
					continue; // Skip missing headers
				}
				else if (typeof value === 'string') {
					eml += `${key}: ${value.replace(/\r?\n/g, `${EOL}  `)}${EOL}`;
				}
				else { // Array
					for (const element of value) {
						eml += `${key}: ${element.replace(/\r?\n/g, `${EOL}  `)}${EOL}`;
					}
				}
			}

			// Start the body
			eml += EOL;

			// Plain text content
			if (data.text) {
				eml += `--${boundary}${EOL}`;
				eml += `Content-Type: text/plain; charset=utf-8${EOL}`;
				eml += EOL;
				eml += data.text;
				eml += EOL + EOL;
			}

			// HTML content
			if (data.html) {
				eml += `--${boundary}${EOL}`;
				eml += `Content-Type: text/html; charset=utf-8${EOL}`;
				eml += EOL;
				eml += data.html;
				eml += EOL + EOL;
			}

			// Append attachments
			if (data.attachments) {
				for (let index = 0; index < data.attachments.length; index++) {
					const attachment = data.attachments[index];
					eml += `--${boundary}${EOL}`;
					eml += `Content-Type: ${attachment.contentType || 'application/octet-stream'}${EOL}`;
					eml += `Content-Transfer-Encoding: base64${EOL}`;
					eml += `Content-Disposition: ${attachment.inline ? 'inline' : 'attachment'}; `;
					eml += `filename="${attachment.filename || attachment.name || (`attachment_${index + 1}`)}"${EOL}`;
					if (attachment.cid) {
						eml += `Content-ID: <${attachment.cid}>${EOL}`;
					}
					eml += EOL;
					if (typeof attachment.data === 'string') {
						const content = Buffer.from(attachment.data).toString('base64');
						eml += wrap(content, 76) + EOL;
					}
					else { // Buffer
						const content = attachment.data.toString('base64');
						eml += wrap(content, 76) + EOL;
					}
					eml += EOL;
				}
			}

			// Finish the boundary
			eml += `--${boundary}--${EOL}`;

			callback(null, eml);
		}
		catch (error) {
			callback(error);
		}
	},

	/**
	 * @param {any} eml
	 * @param {any | Function} options
	 * @param {Function} callback
	 */
	read: (eml, options, callback) => {
		// Shift arguments
		if (typeof options === 'function' && callback === undefined) {
			// eslint-disable-next-line no-param-reassign
			callback = options;
			// eslint-disable-next-line no-param-reassign
			options = null;
		}

		if (typeof callback !== 'function') {
			// eslint-disable-next-line no-param-reassign
			callback = () => {};
		}

		/**
		 * @param {any} data
		 */
		// eslint-disable-next-line no-underscore-dangle
		const _read = data => {
			try {
				/** @type {any} */
				const result = {
					text: '',
					html: '',
				};

				if (data.headers.from) {
					data.headers.From = data.headers.from;
					delete data.headers.from;
				}

				if (data.headers.Date) {
					result.date = new Date(data.headers.Date);
				}
				if (data.headers.Subject) {
					result.subject = EmlParser.unquoteString(data.headers.Subject);
				}
				if (data.headers.From) {
					result.from = EmlParser.getEmailAddress(data.headers.From);
				}
				if (data.headers.To) {
					result.to = EmlParser.getEmailAddress(data.headers.To);
				}
				if (data.headers.CC) {
					result.cc = EmlParser.getEmailAddress(data.headers.CC);
				}
				if (data.headers.Cc) {
					result.cc = EmlParser.getEmailAddress(data.headers.Cc);
				}
				if (data.headers.BCc) {
					result.bcc = EmlParser.getEmailAddress(data.headers.BCc);
				}
				if (data.headers['Reply-To']) {
					result.replyTo = EmlParser.getEmailAddress(data.headers['Reply-To']);
				}
				result.headers = data.headers;

				// Content mime type
				let boundary = null;
				const ct = data.headers['Content-Type'];
				if (ct && /^multipart\//g.test(ct)) {
					const b = EmlParser.getBoundary(ct);
					if (b && b.length > 0) {
						boundary = b;
					}
				}

				if (boundary) {
					for (let index = 0; index < data.body.length; index++) {
						const b = data.body[index];

						// Get the message content
						if (b.part === undefined) {
							console.warn('Warning: undefined b.part');
						}
						else if (typeof b.part === 'string') {
							result.data = b.part;
						}
						else if (b.part.body === undefined) {
							console.warn('Warning: undefined b.part.body');
						}
						else if (typeof b.part.body === 'string') {
							const { headers, body } = b.part;
							appendsBoundary(result, headers, body);
						}
						else {
							for (let indexPart = 0; indexPart < b.part.body.length; indexPart++) {
								if (typeof b.part.body[indexPart] === 'string') {
									result.data = b.part.body[indexPart];
									continue;
								}

								const { headers } = b.part.body[indexPart].part;
								const content = b.part.body[indexPart].part.body;

								appendsBoundary(result, headers, content);
							}
						}
					}
				}
				else if (typeof data.body === 'string') {
					appendsBoundary(result, data.headers, data.body);
				}

				callback(null, result);
			}
			catch (error) {
				callback(error);
			}
		};

		if (typeof eml === 'string') {
			// @ts-ignore
			EmlParser.parse(eml, options, (error, data) => {
				if (error) {
					return callback(error);
				}
				if (!data) {
					return callback(new Error('Cannot parse EML content!'));
				}
				return _read(data);
			});
		}
		else if (typeof eml === 'object') {
			_read(eml);
		}
		else {
			callback(new Error('Missing EML file content!'));
		}
	},

	/**
	 * @param {any} eml
	 * @param {any | Function} options
	 * @param {Function} callback
	 */
	parse: (eml, options, callback) => {
		// Shift arguments
		if (typeof options === 'function' && callback === undefined) {
			// eslint-disable-next-line no-param-reassign
			callback = options;
			// eslint-disable-next-line no-param-reassign
			options = null;
		}

		if (typeof callback !== 'function') {
			// eslint-disable-next-line no-param-reassign
			callback = () => {};
		}

		try {
			if (typeof eml !== 'string') {
				throw new TypeError("Argument 'eml' expected to be string!");
			}

			const lines = eml.split(/\r?\n/);
			const result = {};
			parseRecursive(lines, 0, result, options);
			callback(null, result);
		}
		catch (error) {
			callback(error);
		}
	},

	/** @type {boolean} */
	verbose: false,

	/** @type {Object<string,string>} */
	fileExtensions: {
		'text/plain': '.txt',
		'text/html':  '.html',
		'image/png':  '.png',
		'image/jpg':  '.jpg',
		'image/jpeg': '.jpg',
	},

	/**
	 * Gets file extension by mime type
	 *
	 * @param {string} mimeType
	 * @returns {string}
	 */
	getFileExtension(mimeType) {
		return EmlParser.fileExtensions[mimeType] || '';
	},

	/**
	 * Gets the boundary name
	 *
	 * @param {string} contentType
	 * @returns {string | undefined}
	 */
	getBoundary(contentType) {
		const match = /boundary="?(.+?)"?(\s*;[\S\s]*)?$/g.exec(contentType);
		return match ? match[1] : undefined;
	},

	/**
	 * Gets character set name, e.g. contentType='.....charset="iso-8859-2"....'
	 *
	 * @param {string} contentType
	 * @returns {string | undefined}
	 */
	getCharset(contentType) {
		const match = /charset\s*=\W*([\w-]+)/g.exec(contentType);
		return match ? match[1] : undefined;
	},

	/**
	 * Gets name and e-mail address from a string,
	 * e.g. "PayPal" <noreply@paypal.com> => { name: "PayPal", email: "noreply@paypal.com" }
	 *
	 * @param {string} raw
	 * @returns {any[] | null}
	 */
	getEmailAddress(raw) {
		const list = [];

		// Split around ',' char
		// var parts = raw.split(/,/g); //Will also split ',' inside the quotes
		// var parts = raw.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g); //Ignore ',' within the double quotes
		/** @type {any} */
		const parts = raw.match(/("[^"]*")|[^,]+/g); // Ignore ',' within the double quotes

		for (let index = 0; index < parts.length; index++) {
			/** @type {any} */
			const address = {};

			// Quoted name but without the e-mail address
			if (/^".*"$/g.test(parts[index])) {
				address.name = EmlParser.unquoteString(parts[index]).replace(/"/g, '').trim();
				index++; // Shift to another part to capture e-mail address
			}

			const regex = /^(.*?)(\s*<(.*?)>)$/g;
			const match = regex.exec(parts[index]);
			if (match) {
				const name = EmlParser.unquoteString(match[1]).replace(/"/g, '').trim();
				if (name && name.length > 0) {
					address.name = name;
				}
				address.email = match[3].trim();
				list.push(address);
			}
			else {
				// E-mail address only (without the name)
				address.email = parts[index].trim();
				list.push(address);
			}
		}

		// Return result
		if (list.length === 0) {
			return null; // No e-mail address
		}
		return list; // Multiple e-mail addresses as array
	},

	/**
	 * Builds e-mail address string
	 * e.g. { name: "PayPal", email: "noreply@paypal.com" } => "PayPal" <noreply@paypal.com>
	 *
	 * @param {string | any[] | {name: string, email: string}} data
	 * @returns {string}
	 */
	toEmailAddress(data) {
		let email = '';
		if (data === undefined) {
			// No e-mail address
		}
		else if (typeof data === 'string') {
			email = data;
		}
		else if (typeof data === 'object') {
			if (Array.isArray(data)) {
				for (const datum of data) {
					email += (email.length > 0 ? ', ' : '');
					if (datum.name) {
						email += `"${datum.name}"`;
					}
					if (datum.email) {
						email += `${email.length > 0 ? ' ' : ''}<${datum.email}>`;
					}
				}
			}
			else {
				if (data.name) {
					email += `"${data.name}"`;
				}
				if (data.email) {
					email += `${email.length > 0 ? ' ' : ''}<${data.email}>`;
				}
			}
		}
		return email;
	},

	/**
	 * Decodes string by detecting the charset
	 *
	 * @param {string} s
	 * @returns {string}
	 */
	unquoteString(s) {
		return s.replace(/=\?([^?]+)\?([bq])\?(.+?)\?=/gi, (c, p1, p2, value) => {
			if (value) {
				const charset = getCharsetName(p1 || defaultCharset);
				const type = p2.toUpperCase();

				if (type === 'B') { // Base64
					if (charset === 'utf8') {
						return Buffer.from(value.replace(/\r?\n/g, ''), 'base64').toString('utf8');
					}

					try {
						return iconv.decode(Buffer.from(value.replace(/\r?\n/g, ''), 'base64'), charset);
					}
					catch (error) {
						console.error(error);
						return value;
					}
				}

				if (type === 'Q') { // Quoted printable
					return EmlParser.unquotePrintable(value, charset);
				}
			}

			return c;
		});
	},

	/**
	 * Decodes string like =?UTF-8?B?V2hhdOKAmXMgeW91ciBvbmxpbmUgc2hvcHBpbmcgc3R5bGU/?= or =?UTF-8?Q?...?=
	 *
	 * @param {string} s
	 * @returns {string}
	 */
	unquoteUTF8(s) {
		const regex = /=\?utf-8\?([bq])\?(.+?)(\?=)/gi;
		const match = regex.exec(s);
		if (match) {
			const type = match[1].toUpperCase();
			const value = match[2];
			if (type === 'B') { // Base64
				return Buffer.from(value.replace(/\r?\n/g, ''), 'base64')
					.toString('utf8');
			}
			if (type === 'Q') { // Quoted printable
				return EmlParser.unquotePrintable(value);
			}
		}
		return s;
	},

	/**
	 * Decodes "quoted-printable"
	 *
	 * @param {string} s
	 * @param {string | undefined} [charset]
	 * @returns {string}
	 */
	unquotePrintable(s, charset) {
		// Convert =0D to '\r', =20 to ' ', etc.
		if (!charset || charset === 'utf8' || charset === 'utf-8') {
			return s
				.replace(/=(\w{2})=(\w{2})=(\w{2})/gi, (matcher, p1, p2, p3) => Buffer.from([Number.parseInt(p1, 16), Number.parseInt(p2, 16), Number.parseInt(p3, 16)])
					.toString('utf8'))
				.replace(/=(\w{2})=(\w{2})/gi, (matcher, p1, p2) => Buffer.from([Number.parseInt(p1, 16), Number.parseInt(p2, 16)])
					.toString('utf8'))
				.replace(/=(\w{2})/gi, (matcher, p1) => String.fromCodePoint(Number.parseInt(p1, 16)))
				.replace(/=\r?\n/gi, ''); // Join line
		}

		return s
			.replace(/=(\w{2})=(\w{2})/gi, (matcher, p1, p2) => iconv.decode(Buffer.from([Number.parseInt(p1, 16), Number.parseInt(p2, 16)]), charset))
			.replace(/=(\w{2})/gi, (matcher, p1) => iconv.decode(Buffer.from([Number.parseInt(p1, 16)]), charset))
			.replace(/=\r?\n/gi, ''); // Join line
	},
};

export default EmlParser;
